import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { colorTokens, getSpacing, getTypography } from "../../../designTokens";
import searchIcon from "../../../Assets/icons/Search.svg";
import arrowIcon from "../../../Assets/icons/dropDownIcon.svg";
import checkboxCheckedIcon from "../../../Assets/icons/clickedCheckbox.svg";
import checkboxIcon from "../../../Assets/icons/CheckboxUnchecked.svg";

export interface MultiSelectOption {
  label: string;
  value: string;
}

export interface MultiSelectOptionGroup {
  label: string;
  options: MultiSelectOption[];
}

export interface MultiSelectDropdownProps {
  options: MultiSelectOption[];
  /** When set, options are rendered under non-selectable group headers. */
  optionGroups?: MultiSelectOptionGroup[];
  /** When true with optionGroups, each group header can expand/collapse its options. */
  collapsibleOptionGroups?: boolean;
  selectedValues: string[];
  onSelectionChange: (values: string[]) => void;
  placeholder?: string;
  idPrefix?: string;
  minWidth?: string;
  maxHeight?: string;
  showSearch?: boolean;
  showSelectAll?: boolean;
  /** When true, only one option can be selected; selecting an option replaces the selection. "all" and a specific option are mutually exclusive. */
  singleSelect?: boolean;
  /** When true, "all" and other options are mutually exclusive. Selecting "all" deselects others; selecting any other option deselects "all". Multiple non-"all" options can be selected. */
  allExclusive?: boolean;
  label?: string; // Label that stays visible (e.g., "Opportunity Type")
  showLabelWithSelection?: boolean; // If true, shows label followed by selection (e.g., "Opportunity Type : Option +1")
  error?: boolean;
  helperText?: string;
  /** When true, removes the trigger button border (e.g. for first filter in a row) */
  hideBorder?: boolean;
  /** Variant of the dropdown trigger button */
  variant?: "default" | "transparent";
  /** Optional z-index for the portaled dropdown (use when inside a modal so dropdown appears above it) */
  dropdownZIndex?: number;
  /** Optional max number of options that can be selected; unselected options are disabled when limit is reached */
  maxSelection?: number;
  /** When true, selected values are shown as pill chips in the trigger (design tokens: Background/Bluish, Text/Black, Body/Medium) */
  showChips?: boolean;
  /** When set and showChips is true, only this many chips are shown initially with a "Show more" link to expand. */
  maxChipsDisplay?: number;
  /** When true, trigger behaves like SingleSelectDropdown: when open shows search in trigger, dropdown is options-only (type and search for consistency) */
  searchInTrigger?: boolean;
  /** Optional suffix for placeholder (e.g. red asterisk for required) */
  placeholderSuffix?: React.ReactNode;
  /** When true, the dropdown trigger is disabled and not clickable */
  disabled?: boolean;
  /**
   * When true, empty selection and selecting every option are equivalent (show all).
   * "Select all" clears the selection; options appear checked in show-all mode.
   */
  emptySelectionMeansAll?: boolean;
  /** Called after the user chooses "Select all" (e.g. show a toast). */
  onSelectAll?: () => void;
  /**
   * Optional callback for API-driven search. When provided, parent controls
   * option filtering and local label matching is skipped.
   */
  onSearchChange?: (query: string) => void;
}

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  options,
  optionGroups,
  collapsibleOptionGroups = false,
  selectedValues,
  onSelectionChange,
  placeholder = "Select options",
  idPrefix = "multi-select",
  minWidth = "280px",
  maxHeight = "400px",
  showSearch = true,
  showSelectAll = true,
  singleSelect = false,
  allExclusive = false,
  label,
  showLabelWithSelection = false,
  error = false,
  helperText,
  hideBorder = false,
  variant = "default",
  dropdownZIndex = 10000,
  maxSelection,
  showChips = false,
  maxChipsDisplay,
  searchInTrigger = false,
  placeholderSuffix,
  disabled = false,
  emptySelectionMeansAll = false,
  onSelectAll,
  onSearchChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showAllChips, setShowAllChips] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
    width: number;
    maxHeight?: string;
  } | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownMenuRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(
    {}
  );

  // When searchInTrigger, focus search input when dropdown opens; reset search when closes
  useEffect(() => {
    if (searchInTrigger && isOpen) {
      setSearchQuery("");
      setTimeout(() => searchInputRef.current?.focus(), 0);
    }
    if (searchInTrigger && !isOpen) {
      setSearchQuery("");
    }
  }, [searchInTrigger, isOpen]);

  useEffect(() => {
    if (!onSearchChange) return;
    if (isOpen) {
      setSearchQuery("");
      onSearchChange("");
    }
  }, [isOpen, onSearchChange]);

  const handleSearchInputChange = (value: string): void => {
    setSearchQuery(value);
    onSearchChange?.(value);
  };

  useEffect(() => {
    if (!isOpen) {
      setExpandedGroups({});
      return;
    }
    if (!collapsibleOptionGroups || !optionGroups?.length) return;

    const initialExpanded: Record<string, boolean> = {};
    for (const group of optionGroups) {
      const hasSelected = group.options.some((option) =>
        selectedValues.includes(option.value)
      );
      initialExpanded[group.label] = hasSelected;
    }
    setExpandedGroups(initialExpanded);
  }, [isOpen, collapsibleOptionGroups, optionGroups, selectedValues]);

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  // Compute dropdown position (below by default; open above when more space above). Variant-aware for filter UI.
  const computeDropdownPosition = (): {
    top: number;
    left: number;
    width: number;
    maxHeight: string;
  } | null => {
    if (!triggerRef.current) return null;
    const rect = triggerRef.current.getBoundingClientRect();
    const viewportHeight =
      window.innerHeight || document.documentElement.clientHeight || 0;
    const viewportWidth =
      window.innerWidth || document.documentElement.clientWidth || 0;
    const margin = variant === "transparent" ? 4 : 8;

    const spaceBelow = viewportHeight - rect.bottom - margin;
    const spaceAbove = rect.top - margin;
    const spaceRight = viewportWidth - rect.left - margin;
    // Match My Leads filter: 300px max-height, 212px min width when transparent (filter) variant
    const maxH =
      variant === "transparent" ? 300 : parseInt(maxHeight, 10) || 400;
    const shouldOpenAbove =
      spaceBelow < Math.min(maxH, 240) && spaceAbove > spaceBelow;
    const availableVertical = shouldOpenAbove ? spaceAbove : spaceBelow;
    const effectiveMaxHeight = Math.min(maxH, availableVertical);

    const minWidthPx = parseInt(minWidth, 10) || 280;
    const filterDropdownMinWidth = 212;
    const rawWidth =
      variant === "transparent"
        ? Math.max(rect.width, filterDropdownMinWidth)
        : Math.max(rect.width, minWidthPx);
    const clampedWidth = Math.min(rawWidth, spaceRight);

    // Use actual menu height when available so that when opening above,
    // the bottom edge sits just above the trigger with a constant gap.
    const menuRect = dropdownMenuRef.current?.getBoundingClientRect();
    const menuHeight = menuRect?.height ?? effectiveMaxHeight;

    const top = shouldOpenAbove
      ? Math.max(rect.top - menuHeight - margin, margin)
      : rect.bottom + margin;

    return {
      top,
      left: rect.left,
      width: clampedWidth,
      maxHeight: `${effectiveMaxHeight}px`,
    };
  };

  // Position dropdown for portal (opens below by default, above when there's more space above)
  useLayoutEffect(() => {
    if (!isOpen) {
      setDropdownPosition(null);
      return;
    }
    const pos = computeDropdownPosition();
    if (pos) {
      setDropdownPosition(pos);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- computeDropdownPosition closes over refs; maxHeight/minWidth/variant already in deps
  }, [isOpen, maxHeight, minWidth, variant]);

  // Keep dropdown position in sync when page scrolls or window resizes
  useEffect(() => {
    if (!isOpen) return;
    let rafId: number;
    const updatePosition = () => {
      const pos = computeDropdownPosition();
      if (pos) {
        setDropdownPosition(pos);
      }
      rafId = requestAnimationFrame(updatePosition);
    };
    rafId = requestAnimationFrame(updatePosition);
    const onResize = () => {
      const pos = computeDropdownPosition();
      if (pos) {
        setDropdownPosition(pos);
      }
    };
    window.addEventListener("resize", onResize);
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", onResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- computeDropdownPosition closes over refs; maxHeight/minWidth/variant already in deps
  }, [isOpen, maxHeight, minWidth, variant]);

  // Close dropdown when clicking outside (trigger or portaled menu)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const inTrigger = triggerRef.current?.contains(target);
      const inMenu = dropdownMenuRef.current?.contains(target);
      if (!inTrigger && !inMenu) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const resolvedOptions =
    optionGroups && optionGroups.length > 0
      ? optionGroups.flatMap((group) => group.options)
      : options;

  const normalizedSearchQuery = searchQuery.toLowerCase();

  // Filter options based on search query unless the parent drives search via API
  const filteredOptions = onSearchChange
    ? resolvedOptions
    : resolvedOptions.filter((option) =>
        option.label.toLowerCase().includes(normalizedSearchQuery)
      );

  const filteredOptionGroups =
    optionGroups && optionGroups.length > 0
      ? optionGroups
          .map((group) => ({
            ...group,
            options: onSearchChange
              ? group.options
              : group.options.filter((option) =>
                  option.label.toLowerCase().includes(normalizedSearchQuery)
                ),
          }))
          .filter((group) => group.options.length > 0)
      : null;

  useEffect(() => {
    if (
      !isOpen ||
      !collapsibleOptionGroups ||
      !normalizedSearchQuery ||
      !filteredOptionGroups
    ) {
      return;
    }
    setExpandedGroups((prev) => {
      const next = { ...prev };
      for (const group of filteredOptionGroups) {
        next[group.label] = true;
      }
      return next;
    });
  }, [
    isOpen,
    collapsibleOptionGroups,
    normalizedSearchQuery,
    filteredOptionGroups,
  ]);

  const toggleOptionGroup = (groupLabel: string) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupLabel]: !prev[groupLabel],
    }));
  };

  const isOptionGroupExpanded = (groupLabel: string) =>
    !collapsibleOptionGroups || expandedGroups[groupLabel] === true;

  const handleToggleGroupSelection = (
    event: React.MouseEvent,
    groupOptions: MultiSelectOption[]
  ) => {
    event.stopPropagation();
    if (singleSelect || groupOptions.length === 0) return;

    const groupValues = groupOptions.map((option) => option.value);
    const allGroupSelected = groupValues.every((value) =>
      selectedValues.includes(value)
    );

    if (allGroupSelected) {
      onSelectionChange(
        selectedValues.filter((value) => !groupValues.includes(value))
      );
      return;
    }

    const valuesToAdd = groupValues.filter(
      (value) => !selectedValues.includes(value)
    );
    let nextSelected = [...selectedValues, ...valuesToAdd];
    if (maxSelection != null && nextSelected.length > maxSelection) {
      nextSelected = nextSelected.slice(0, maxSelection);
    }
    if (allExclusive) {
      nextSelected = nextSelected.filter((value) => value !== "all");
    }
    onSelectionChange(nextSelected);
  };

  // Check if every option is explicitly selected (empty selection = no checks, still shows all data via API)
  const allSelected =
    resolvedOptions.length > 0 &&
    resolvedOptions.every((option) => selectedValues.includes(option.value));

  const isSelectAllChecked = allSelected;

  const handleClearSelection = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (selectedValues.length === 0) return;
    onSelectionChange([]);
  };

  // Handle individual option toggle
  const handleToggle = (value: string) => {
    if (singleSelect) {
      onSelectionChange(selectedValues.includes(value) ? [] : [value]);
      return;
    } else if (allExclusive) {
      // "all" and other options mutually exclusive; multiple specific options allowed
      if (value === "all") {
        onSelectionChange(["all"]);
      } else if (selectedValues.includes("all")) {
        // had "all", replace with just this specific option
        onSelectionChange([value]);
      } else if (selectedValues.includes(value)) {
        const next = selectedValues.filter((v) => v !== value);
        onSelectionChange(next);
      } else {
        onSelectionChange([...selectedValues, value]);
      }
    } else if (selectedValues.includes(value)) {
      onSelectionChange(selectedValues.filter((v) => v !== value));
    } else {
      if (maxSelection != null && selectedValues.length >= maxSelection) {
        return;
      }
      onSelectionChange([...selectedValues, value]);
    }
  };

  // Handle select all toggle
  const handleSelectAll = () => {
    if (emptySelectionMeansAll) {
      if (allSelected) {
        onSelectionChange([]);
        return;
      }
      const allValues = resolvedOptions.map((opt) => opt.value);
      const capped =
        maxSelection != null ? allValues.slice(0, maxSelection) : allValues;
      onSelectionChange(capped);
      onSelectAll?.();
      return;
    }
    if (singleSelect || allExclusive) {
      onSelectionChange(["all"]);
    } else if (allSelected) {
      onSelectionChange([]);
    } else {
      const allValues = resolvedOptions.map((opt) => opt.value);
      const capped =
        maxSelection != null ? allValues.slice(0, maxSelection) : allValues;
      onSelectionChange(capped);
    }
  };

  // Get display content for the button
  type DisplayContent =
    | { text: string }
    | { label: string; selection: string | null; remainingCount: number };

  const getDisplayContent = (): DisplayContent => {
    if (showLabelWithSelection && label) {
      // Show label with selection format: "Label : Option +X"
      if (
        selectedValues.length === 0 ||
        (emptySelectionMeansAll && allSelected)
      ) {
        return {
          label,
          selection: emptySelectionMeansAll && allSelected ? "All" : null,
          remainingCount: 0,
        };
      }

      // Filter out "all" and empty (e.g. "All Types") options - when only "all" selected, show no selection (filter not "visible")
      const nonAllValues = selectedValues.filter(
        (v) => v !== "all" && v !== ""
      );
      if (nonAllValues.length === 0) {
        return { label, selection: null, remainingCount: 0 };
      }

      // Get the first selected option label
      const firstSelectedOption = resolvedOptions.find(
        (opt) => opt.value === nonAllValues[0]
      );
      const firstOptionLabel = firstSelectedOption?.label || "";

      // If only one option selected
      if (nonAllValues.length === 1) {
        return { label, selection: firstOptionLabel, remainingCount: 0 };
      }

      // If multiple options selected, show first option + count
      const remainingCount = nonAllValues.length - 1;
      return { label, selection: firstOptionLabel, remainingCount };
    }

    // Default behavior (no label) - return as text
    if (selectedValues.length === 0) {
      return { text: placeholder };
    }
    if (selectedValues.length === 1) {
      const selectedOption = resolvedOptions.find(
        (opt) => opt.value === selectedValues[0]
      );
      return { text: selectedOption?.label || placeholder };
    }
    return { text: `${selectedValues.length} selected` };
  };

  const displayContent = getDisplayContent();

  // When chips render below the field, keep the trigger as the placeholder
  // (e.g. "Services") instead of "N selected".
  const triggerContent = showChips
    ? placeholder
    : selectedValues.length === 0
      ? placeholder
      : selectedValues.length === 1
        ? (resolvedOptions.find((o) => o.value === selectedValues[0])?.label ??
          placeholder)
        : `${selectedValues.length} selected`;

  return (
    <div style={{ position: "relative", width: "100%", minWidth: 0 }}>
      {/* Floating label when searchInTrigger (like SingleSelectDropdown) */}
      {searchInTrigger && label && (
        <label
          htmlFor={`${idPrefix}-button`}
          style={{
            ...getTypography("Body/Medium"),
            color: colorTokens["Text/Grey"],
            position: "absolute",
            top: "-10px",
            left: getSpacing("12px"),
            backgroundColor: colorTokens["Background/White"],
            padding: `0 ${getSpacing("4px")}`,
            zIndex: 1,
            display: isOpen || selectedValues.length > 0 ? "block" : "none",
          }}
        >
          {label}
          {placeholderSuffix}
        </label>
      )}

      <div
        ref={triggerRef}
        style={{ position: "relative", width: "100%", minWidth: 0 }}
      >
        {searchInTrigger ? (
          <>
            {isOpen ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: getSpacing("8px"),
                  padding: `${getSpacing("12px")} ${getSpacing("40px")} ${getSpacing("12px")} ${getSpacing("16px")}`,
                  borderRadius: getSpacing("8px"),
                  border: `1px solid ${
                    error
                      ? colorTokens["Alert/Red/Dark"]
                      : colorTokens["Primary/Default"]
                  }`,
                  backgroundColor: colorTokens["Background/White"],
                  minHeight: "48px",
                  boxSizing: "border-box",
                }}
              >
                <img
                  src={searchIcon}
                  alt="Search"
                  style={{
                    width: "14px",
                    height: "14px",
                    opacity: 0.6,
                    flexShrink: 0,
                  }}
                />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search"
                  value={searchQuery}
                  onChange={(e) => handleSearchInputChange(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  style={{
                    flex: 1,
                    border: "none",
                    outline: "none",
                    ...getTypography("Body/Medium"),
                    fontSize: "14px",
                    color: colorTokens["Text/Black"],
                    backgroundColor: "transparent",
                  }}
                  id={`${idPrefix}-search-input`}
                  aria-label="Search options"
                  aria-controls={`${idPrefix}-dropdown`}
                />
              </div>
            ) : (
              <button
                type="button"
                disabled={disabled}
                onClick={() => !disabled && setIsOpen(true)}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                style={{
                  width: "100%",
                  padding: `${getSpacing("12px")} ${getSpacing("40px")} ${getSpacing("12px")} ${getSpacing("16px")}`,
                  borderRadius: getSpacing("8px"),
                  border: `1px solid ${
                    error
                      ? colorTokens["Alert/Red/Dark"]
                      : colorTokens["Borders/Light"]
                  }`,
                  backgroundColor: colorTokens["Background/White"],
                  ...getTypography("Body/Medium"),
                  color:
                    !showChips && selectedValues.length > 0
                      ? colorTokens["Text/Black"]
                      : colorTokens["Text/Light Grey"],
                  cursor: disabled ? "not-allowed" : "pointer",
                  opacity: disabled ? 0.6 : 1,
                  textAlign: "left",
                  boxSizing: "border-box",
                  minHeight: "48px",
                  display: "flex",
                  alignItems: "center",
                }}
                id={`${idPrefix}-button`}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                aria-label={label || placeholder}
              >
                <span style={{ flex: 1, textAlign: "left" }}>
                  {triggerContent}
                  {selectedValues.length === 0 && placeholderSuffix}
                </span>
              </button>
            )}
            <img
              src={arrowIcon}
              alt=""
              aria-hidden="true"
              style={{
                position: "absolute",
                right: getSpacing("12px"),
                top: "50%",
                transform: `translateY(-50%) ${isOpen ? "rotate(180deg)" : ""}`,
                width: "16px",
                height: "16px",
                pointerEvents: "none",
                zIndex: 1,
                opacity: 0.6,
              }}
            />
          </>
        ) : (
          <button
            type="button"
            disabled={disabled}
            onClick={() => !disabled && setIsOpen(!isOpen)}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            style={{
              paddingTop:
                variant === "transparent"
                  ? getSpacing("8px")
                  : getSpacing("12px"),
              paddingBottom:
                variant === "transparent"
                  ? getSpacing("8px")
                  : getSpacing("12px"),
              paddingLeft:
                variant === "transparent"
                  ? getSpacing("12px")
                  : getSpacing("16px"),
              paddingRight:
                variant === "transparent"
                  ? getSpacing("12px")
                  : getSpacing("12px"),
              borderRadius: getSpacing("8px"),
              border:
                variant === "transparent"
                  ? "none"
                  : hideBorder
                    ? "none"
                    : error
                      ? `1px solid ${colorTokens["Alert/Red/Dark"]}`
                      : `1px solid ${colorTokens["Borders/Light"]}`,
              backgroundColor:
                variant === "transparent"
                  ? isHovered
                    ? colorTokens["Background/Greyish"]
                    : "transparent"
                  : colorTokens["Background/White"],
              ...getTypography("Body/Medium"),
              color: colorTokens["Text/Black"],
              cursor: disabled ? "not-allowed" : "pointer",
              opacity: disabled ? 0.6 : 1,
              width: variant === "transparent" ? "auto" : "100%",
              minWidth: variant === "transparent" ? undefined : "100%",
              height: variant === "transparent" ? "auto" : "48px",
              boxSizing: "border-box",
              display: "flex",
              alignItems: "center",
              justifyContent:
                variant === "transparent" ? "flex-start" : "space-between",
              gap: getSpacing("8px"),
              transition: "background-color 0.2s",
            }}
            id={`${idPrefix}-button`}
            aria-label={
              variant === "transparent" && label
                ? `Filter by ${label}`
                : label || placeholder
            }
            aria-haspopup="listbox"
            aria-expanded={isOpen}
          >
            <div
              style={{
                textAlign: "left",
                display: "flex",
                alignItems: "center",
                gap: getSpacing("8px"),
                flexWrap: "wrap",
                flex: variant === "transparent" ? undefined : 1,
              }}
            >
              {showChips ? (
                <span style={{ color: colorTokens["Text/Light Grey"] }}>
                  {placeholder}
                </span>
              ) : "text" in displayContent ? (
                <span>{displayContent.text}</span>
              ) : (
                <>
                  <span
                    style={{
                      ...(variant === "transparent"
                        ? getTypography("Body/Medium")
                        : getTypography("Links/Btn Text")),
                      color: colorTokens["Text/Black"],
                    }}
                  >
                    {displayContent.label}
                  </span>
                  {displayContent.selection !== null && (
                    <>
                      <span style={{ color: colorTokens["Text/Black"] }}>
                        :
                      </span>
                      <span style={{ color: colorTokens["Primary/Default"] }}>
                        {displayContent.selection}
                      </span>
                      {displayContent.remainingCount > 0 && (
                        <span
                          style={{
                            backgroundColor: colorTokens["Background/Greyish"],
                            borderRadius: getSpacing("8px"),
                            padding: `0 ${getSpacing("8px")}`,
                            height: "20px",
                            display: "flex",
                            alignItems: "center",
                            fontSize: "12px",
                            color: colorTokens["Text/Black"],
                          }}
                        >
                          +{displayContent.remainingCount}
                        </span>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
            <img
              src={arrowIcon}
              alt=""
              style={{
                width: "16px",
                height: "16px",
                transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.2s",
                opacity: 0.6,
                flexShrink: 0,
              }}
            />
          </button>
        )}
      </div>

      {showChips &&
        selectedValues.length > 0 &&
        (() => {
          const limit =
            maxChipsDisplay != null && !showAllChips
              ? maxChipsDisplay
              : selectedValues.length;
          const valuesToShow = selectedValues.slice(0, limit);
          const hasMore =
            maxChipsDisplay != null &&
            selectedValues.length > maxChipsDisplay &&
            !showAllChips;
          return (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: getSpacing("8px"),
                marginTop: getSpacing("8px"),
              }}
              id={`${idPrefix}-chips`}
            >
              {valuesToShow.map((value) => {
                const label =
                  resolvedOptions.find((opt) => opt.value === value)?.label ??
                  value;
                return (
                  <span
                    key={value}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: getSpacing("8px"),
                      padding: `${getSpacing("4px")} ${getSpacing("12px")}`,
                      borderRadius: getSpacing("20px"),
                      backgroundColor: colorTokens["Background/Bluish"],
                      color: colorTokens["Text/Black"],
                      ...getTypography("Body/Medium"),
                    }}
                  >
                    {label}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectionChange(
                          selectedValues.filter((v) => v !== value)
                        );
                      }}
                      aria-label={`Remove ${label}`}
                      style={{
                        width: getSpacing("16px"),
                        height: getSpacing("16px"),
                        borderRadius: getSpacing("8px"),
                        border: "none",
                        background: "transparent",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: 0,
                      }}
                      id={`${idPrefix}-chip-remove-${value.replace(/\s+/g, "-").toLowerCase()}`}
                    >
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M9 3L3 9M3 3L9 9"
                          stroke={colorTokens["Text/Black"]}
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </span>
                );
              })}
              {hasMore && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowAllChips(true);
                  }}
                  style={{
                    ...getTypography("Body/Medium"),
                    color: colorTokens["Primary/Default"],
                    background: "transparent",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                    textDecoration: "underline",
                  }}
                  id={`${idPrefix}-chips-show-more`}
                >
                  Show more
                </button>
              )}
            </div>
          );
        })()}

      {helperText && (
        <p
          style={{
            marginTop: getSpacing("4px"),
            marginLeft: getSpacing("16px"),
            marginRight: getSpacing("16px"),
            ...getTypography("Body/Small"),
            color: error
              ? colorTokens["Alert/Red/Dark"]
              : colorTokens["Text/Light Grey"],
          }}
        >
          {helperText}
        </p>
      )}

      {/* Dropdown Menu - portaled so it's clickable above overflow/stacking contexts */}
      {isOpen &&
        dropdownPosition &&
        createPortal(
          <div
            ref={dropdownMenuRef}
            style={{
              position: "fixed",
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: dropdownPosition.width,
              ...(variant === "transparent"
                ? { minWidth: "212px" }
                : minWidth !== "100%"
                  ? { minWidth }
                  : {}),
              maxHeight: dropdownPosition.maxHeight || maxHeight,
              backgroundColor: colorTokens["Background/White"],
              border: `1px solid ${colorTokens["Borders/Light"]}`,
              borderRadius: getSpacing("8px"),
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
              display: "flex",
              flexDirection: "column",
              zIndex: dropdownZIndex,
              pointerEvents: "auto",
              overflow: "hidden",
            }}
            id={`${idPrefix}-dropdown`}
          >
            {/* Search Bar inside dropdown - only when not searchInTrigger (search is in trigger) */}
            {showSearch && !searchInTrigger && (
              <div
                style={{
                  padding: getSpacing("8px"),
                  flexShrink: 0,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: getSpacing("8px"),
                    padding: `${getSpacing("8px")} ${getSpacing("12px")}`,
                    borderRadius: "20px",
                    border: `1px solid ${colorTokens["Borders/Light"]}`,
                    backgroundColor: colorTokens["Background/White"],
                  }}
                >
                  <img
                    src={searchIcon}
                    alt="Search"
                    style={{ width: "14px", height: "14px", opacity: 0.6 }}
                  />
                  <input
                    type="text"
                    placeholder="Search"
                    value={searchQuery}
                    onChange={(e) => handleSearchInputChange(e.target.value)}
                    style={{
                      flex: 1,
                      border: "none",
                      outline: "none",
                      ...getTypography("Body/Medium"),
                      fontSize: "14px",
                      color: colorTokens["Text/Black"],
                      backgroundColor: "transparent",
                    }}
                    id={`${idPrefix}-search-input`}
                  />
                </div>
              </div>
            )}

            {/* Options List */}
            <div
              style={{
                padding: getSpacing("8px"),
                overflowY: "auto",
                flex: 1,
                minHeight: 0,
                ...(searchInTrigger ? { maxHeight: "300px" } : {}),
              }}
              className="custom-scrollbar"
            >
              {/* Select All + Clear all */}
              {showSelectAll && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: getSpacing("8px"),
                    padding:
                      variant === "transparent"
                        ? `${getSpacing("8px")} ${getSpacing("12px")}`
                        : getSpacing("8px"),
                    marginBottom: getSpacing("4px"),
                    borderRadius: getSpacing("4px"),
                  }}
                  id={`${idPrefix}-select-all-row`}
                >
                  <div
                    onClick={handleSelectAll}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: getSpacing("8px"),
                      flex: 1,
                      minWidth: 0,
                      cursor: "pointer",
                      borderRadius: getSpacing("4px"),
                      transition: "background-color 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor =
                        colorTokens["Background/Greyish"];
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }}
                    id={`${idPrefix}-option-select-all`}
                    role="option"
                    aria-selected={isSelectAllChecked}
                  >
                    <img
                      src={
                        isSelectAllChecked ? checkboxCheckedIcon : checkboxIcon
                      }
                      alt=""
                      aria-hidden="true"
                      style={{ width: "16px", height: "16px", flexShrink: 0 }}
                    />
                    <span
                      style={{
                        ...getTypography("Body/Medium"),
                        color: colorTokens["Text/Black"],
                      }}
                    >
                      Select all
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={handleClearSelection}
                    disabled={selectedValues.length === 0}
                    id={`${idPrefix}-clear-selection`}
                    aria-label="Clear all"
                    style={{
                      flexShrink: 0,
                      border: "none",
                      background: "transparent",
                      padding: 0,
                      cursor:
                        selectedValues.length === 0 ? "not-allowed" : "pointer",
                      ...getTypography("Body/Small"),
                      color:
                        selectedValues.length === 0
                          ? colorTokens["Text/Light Grey"]
                          : colorTokens["Primary/Default"],
                      opacity: selectedValues.length === 0 ? 0.5 : 1,
                    }}
                  >
                    Clear all
                  </button>
                </div>
              )}

              {/* Individual Options */}
              {filteredOptionGroups
                ? filteredOptionGroups.map((group) => {
                    const groupExpanded = isOptionGroupExpanded(group.label);
                    const selectedCountInGroup = group.options.filter(
                      (option) => selectedValues.includes(option.value)
                    ).length;
                    const allGroupSelected =
                      group.options.length > 0 &&
                      selectedCountInGroup === group.options.length;
                    const groupIdSuffix = group.label
                      .replace(/\s+/g, "-")
                      .toLowerCase();
                    return (
                      <div key={group.label}>
                        <div
                          onClick={
                            collapsibleOptionGroups
                              ? () => toggleOptionGroup(group.label)
                              : undefined
                          }
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: getSpacing("8px"),
                            ...getTypography("Body/Small"),
                            color: colorTokens["Text/Grey"],
                            fontWeight: 600,
                            padding:
                              variant === "transparent"
                                ? `${getSpacing("8px")} ${getSpacing("12px")}`
                                : getSpacing("8px"),
                            cursor: collapsibleOptionGroups
                              ? "pointer"
                              : "default",
                            borderRadius: getSpacing("4px"),
                            transition: "background-color 0.2s",
                          }}
                          onMouseEnter={(e) => {
                            if (collapsibleOptionGroups) {
                              e.currentTarget.style.backgroundColor =
                                colorTokens["Background/Greyish"];
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor =
                              "transparent";
                          }}
                          id={`${idPrefix}-group-${groupIdSuffix}`}
                          role={collapsibleOptionGroups ? "button" : undefined}
                          aria-expanded={
                            collapsibleOptionGroups ? groupExpanded : undefined
                          }
                        >
                          <span
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: getSpacing("8px"),
                            }}
                          >
                            {collapsibleOptionGroups && (
                              <button
                                type="button"
                                id={`${idPrefix}-group-${groupIdSuffix}-select-all`}
                                aria-label={`Select all ${group.label} stages`}
                                aria-pressed={allGroupSelected}
                                onClick={(event) =>
                                  handleToggleGroupSelection(
                                    event,
                                    group.options
                                  )
                                }
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  padding: 0,
                                  border: "none",
                                  background: "transparent",
                                  cursor: "pointer",
                                  flexShrink: 0,
                                }}
                              >
                                <img
                                  src={
                                    allGroupSelected
                                      ? checkboxCheckedIcon
                                      : checkboxIcon
                                  }
                                  alt=""
                                  aria-hidden="true"
                                  style={{
                                    width: "16px",
                                    height: "16px",
                                  }}
                                />
                              </button>
                            )}
                            <span>{group.label}</span>
                            {selectedCountInGroup > 0 && (
                              <span
                                style={{
                                  ...getTypography("Body/Small"),
                                  color: colorTokens["Primary/Default"],
                                  fontWeight: 500,
                                }}
                              >
                                ({selectedCountInGroup})
                              </span>
                            )}
                          </span>
                          <span
                            style={{
                              ...getTypography("Body/Small"),
                              color: colorTokens["Text/Light Grey"],
                              fontWeight: 500,
                            }}
                          >
                            {group.options.length}
                          </span>
                        </div>
                        {groupExpanded &&
                          group.options.map((option) => {
                            const isSelected = selectedValues.includes(
                              option.value
                            );
                            const isDisabled =
                              maxSelection != null &&
                              !isSelected &&
                              selectedValues.length >= maxSelection;
                            return (
                              <div
                                key={option.value}
                                onClick={() =>
                                  !isDisabled && handleToggle(option.value)
                                }
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: getSpacing("8px"),
                                  padding:
                                    variant === "transparent"
                                      ? `${getSpacing("8px")} ${getSpacing("12px")} ${getSpacing("8px")} ${getSpacing("24px")}`
                                      : `${getSpacing("8px")} ${getSpacing("8px")} ${getSpacing("8px")} ${getSpacing("24px")}`,
                                  marginBottom: getSpacing("4px"),
                                  cursor: isDisabled
                                    ? "not-allowed"
                                    : "pointer",
                                  borderRadius: getSpacing("4px"),
                                  transition: "background-color 0.2s",
                                  opacity: isDisabled ? 0.5 : 1,
                                }}
                                onMouseEnter={(e) => {
                                  if (!isDisabled) {
                                    e.currentTarget.style.backgroundColor =
                                      colorTokens["Background/Greyish"];
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor =
                                    "transparent";
                                }}
                                id={`${idPrefix}-option-${option.value.replace(/\s+/g, "-").toLowerCase()}`}
                              >
                                <img
                                  src={
                                    isSelected
                                      ? checkboxCheckedIcon
                                      : checkboxIcon
                                  }
                                  alt="Checkbox"
                                  style={{ width: "16px", height: "16px" }}
                                />
                                <span
                                  style={{
                                    flex: 1,
                                    ...getTypography("Body/Medium"),
                                    color: isDisabled
                                      ? colorTokens["Text/Light Grey"]
                                      : colorTokens["Text/Black"],
                                    cursor: isDisabled
                                      ? "not-allowed"
                                      : "pointer",
                                  }}
                                >
                                  {option.label}
                                </span>
                              </div>
                            );
                          })}
                      </div>
                    );
                  })
                : filteredOptions.map((option) => {
                    const isSelected = selectedValues.includes(option.value);
                    const isDisabled =
                      maxSelection != null &&
                      !isSelected &&
                      selectedValues.length >= maxSelection;
                    return (
                      <div
                        key={option.value}
                        onClick={() =>
                          !isDisabled && handleToggle(option.value)
                        }
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: getSpacing("8px"),
                          padding:
                            variant === "transparent"
                              ? `${getSpacing("8px")} ${getSpacing("12px")}`
                              : getSpacing("8px"),
                          marginBottom: getSpacing("4px"),
                          cursor: isDisabled ? "not-allowed" : "pointer",
                          borderRadius: getSpacing("4px"),
                          transition: "background-color 0.2s",
                          opacity: isDisabled ? 0.5 : 1,
                        }}
                        onMouseEnter={(e) => {
                          if (!isDisabled) {
                            e.currentTarget.style.backgroundColor =
                              colorTokens["Background/Greyish"];
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "transparent";
                        }}
                        id={`${idPrefix}-option-${option.value.replace(/\s+/g, "-").toLowerCase()}`}
                      >
                        <img
                          src={isSelected ? checkboxCheckedIcon : checkboxIcon}
                          alt="Checkbox"
                          style={{ width: "16px", height: "16px" }}
                        />
                        <span
                          style={{
                            flex: 1,
                            ...getTypography("Body/Medium"),
                            color: isDisabled
                              ? colorTokens["Text/Light Grey"]
                              : colorTokens["Text/Black"],
                            cursor: isDisabled ? "not-allowed" : "pointer",
                          }}
                        >
                          {option.label}
                        </span>
                      </div>
                    );
                  })}

              {(filteredOptionGroups
                ? filteredOptionGroups.length === 0
                : filteredOptions.length === 0) && (
                <div
                  style={{
                    ...getTypography("Body/Medium"),
                    color: colorTokens["Text/Light Grey"],
                    textAlign: "center",
                    padding: getSpacing("16px"),
                  }}
                >
                  No options found
                </div>
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default MultiSelectDropdown;
